import { useCallback, useEffect, useMemo, useRef, useState } from 'preact/hooks';
import { Button } from '@core/components/Elements/Button';
import { Spinner } from '@core/components/Elements';
import { QrCodeGenerator } from '@core/components/QrCodeGenerator';
import useUserDevice from '@core/hooks/useUserDevice';
import useWindowDimensions from '@core/hooks/useWindowDimensions';
import emitter from '@webcore/core/events/eventemitter';

// Module-level flag so we don't miss the `initialized` event if it fires
// before any EjcLaunchButton instance mounts. Set once per page load.
let appInitialized = false;
emitter.on('initialized', () => {
  appInitialized = true;
});

interface EjcLaunchButtonProps {
  /** Called when the user taps the button on a mobile device. Each boilerplate
   *  decides what happens here (e.g. emit "xr-start" for 8thwall, or call
   *  xrSession.start() for WebXR). */
  onLaunch: () => void;
  /** Button label (default: "View in AR"). */
  label?: string;
  /** When true, the mobile launch path is suppressed and the button is greyed
   *  out. Desktop QR popup still works so users can scan with a phone. */
  disabled?: boolean;
}

export function EjcLaunchButton({ onLaunch, label = 'View in AR', disabled = false }: EjcLaunchButtonProps) {
  const { isMobile, device } = useUserDevice();
  const { width } = useWindowDimensions();
  const [showQRPopup, setShowQRPopup] = useState(false);
  const [isAppReady, setIsAppReady] = useState(appInitialized);
  const qrContainerRef = useRef<HTMLDivElement>(null);
  const buttonWrapperRef = useRef<HTMLDivElement>(null);
  const didAutoOpenQR = useRef(false);
  const [popupWidth, setPopupWidth] = useState(240);

  // Desktop only: surface the QR popup straight away on load so users don't have
  // to click the launch button first. `useUserDevice` resolves `device`/`isMobile`
  // in an effect (both start desktop-ish), so we wait for the device to actually
  // resolve — otherwise the white popup flashes for a frame on mobile before it's
  // hidden. Fires once, so a user dismissal (click-outside) sticks.
  useEffect(() => {
    if (didAutoOpenQR.current) return;
    if (device && !isMobile) {
      setShowQRPopup(true);
      didAutoOpenQR.current = true;
    }
  }, [device, isMobile]);

  useEffect(() => {
    if (appInitialized) {
      setIsAppReady(true);
      return;
    }
    const onReady = () => setIsAppReady(true);
    emitter.on('initialized', onReady);
    return () => emitter.off('initialized', onReady);
  }, []);

  const qrUrl = useMemo(() => {
    try {
      return window.location.href;
    } catch {
      return '';
    }
  }, []);

  const handleLaunch = useCallback(() => {
    if (!isMobile) {
      setShowQRPopup(true);
      return;
    }
    if (disabled) return;
    onLaunch();
  }, [disabled, isMobile, onLaunch]);

  useEffect(() => {
    if (isMobile || !showQRPopup) return;

    const handleOutsideClick = (event: PointerEvent) => {
      if (!qrContainerRef.current?.contains(event.target as Node)) {
        setShowQRPopup(false);
      }
    };
    document.addEventListener('pointerdown', handleOutsideClick);
    return () => document.removeEventListener('pointerdown', handleOutsideClick);
  }, [isMobile, showQRPopup]);

  useEffect(() => {
    if (isMobile) return;
    const measure = () => {
      const measuredWidth = buttonWrapperRef.current?.getBoundingClientRect().width;
      if (measuredWidth && measuredWidth > 0) {
        setPopupWidth(Math.round(measuredWidth));
      }
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [isMobile, width]);

  const qrSize = Math.max(120, popupWidth - 32);

  return (
    <div ref={qrContainerRef} className="relative flex flex-col justify-center items-center">
      <div ref={buttonWrapperRef} className="w-full">
        {isAppReady ? (
          <Button
            className="!rounded-full text-lg font-[500] duration-75 !bg-transparent border-2 border-white text-white active:!bg-white active:!text-black focus:!bg-white focus:!text-black md:hover:!bg-white md:hover:!text-black disabled:!opacity-50"
            onClick={handleLaunch}
            disabled={disabled}
          >
            {label}
          </Button>
        ) : (
          <div className="h-[58px] flex items-center justify-center">
            <Spinner size="2md" variant="light" />
          </div>
        )}
      </div>

      {!isMobile && (
        <div
          className={`absolute bottom-full left-1/2 mb-10 z-50 -translate-x-1/2 transform transition-all duration-150 ease-out ${
            showQRPopup
              ? 'translate-y-0 opacity-100 pointer-events-auto'
              : 'translate-y-5 opacity-0 pointer-events-none'
          }`}
        >
          <div
            className="bg-white rounded-[8px] flex flex-col items-center pt-1 px-1 pb-5"
            style={{
              width: `${popupWidth}px`,
              boxSizing: 'border-box',
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            }}
          >
            <QrCodeGenerator value={qrUrl} fgColor="#000000" size={qrSize} />
            <p className="text-black text-center text-lg tracking-[1.76px] font-[500] mt-2 mb-1">
              Scan QR code
            </p>
          </div>
          <div
            className="absolute left-1/2 -translate-x-1/2 -bottom-[18px] w-0 h-0"
            style={{
              borderLeft: '10px solid transparent',
              borderRight: '10px solid transparent',
              borderTop: '18px solid white',
            }}
          />
        </div>
      )}
    </div>
  );
}

export default EjcLaunchButton;
